<template>
    <section class="ebc-panel">
        <div class="ebc-panel__head">
            <div>
                <h3 class="ebc-panel__title">사용 패턴</h3>
                <span class="ebc-panel__sub">요일 × 4시간 평균 사용량</span>
            </div>
            <span v-if="peakCell" class="heatmap-peak ebc-num">
                최다 {{ peakCell.dow }} {{ peakCell.block }}
            </span>
        </div>

        <div class="ebc-panel__body">
            <EChart v-if="cells.length" :option="option" />
            <div v-else class="ebc-empty">
                <i :class="gridLoading ? 'fas fa-spinner fa-spin' : 'fas fa-th'"></i>
                <span>{{ gridLoading ? "시간대별 사용량을 불러오는 중..." : gridError || "시간대별 사용량이 없습니다" }}</span>
            </div>
        </div>
    </section>
</template>

<script>
import EChart from "./EChart.vue";
import { chartColors, grayScale, tooltipStyle, tooltipHtml } from "../../utils/chartTheme.js";
import { axisLabel, navyRamp } from "./analysisChart.js";
import { num } from "./analysisFormat.js";

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
const BLOCK_HOURS = 4;
const BLOCKS = Array.from({ length: 24 / BLOCK_HOURS }, (_, i) => `${i * BLOCK_HOURS}–${(i + 1) * BLOCK_HOURS}시`);

export default {
    name: "UsageHeatmapPanel",
    components: { EChart },
    props: {
        grid: {
            type: Object,
            default: null,
        },
        gridLoading: {
            type: Boolean,
            default: false,
        },
        gridError: {
            type: String,
            default: "",
        },
        unit: {
            type: String,
            default: "kWh",
        },
    },
    computed: {
        /** 일자별 4시간 합계를 요일별로 평균 → [요일, 구간, 평균, 일수]. hour는 구간 끝 시각(1~24) */
        cells() {
            const rows = this.grid?.grid || [];
            const columns = this.grid?.columns || [];
            if (!rows.length || !columns.length) return [];
            const sums = BLOCKS.map(() => WEEKDAYS.map(() => ({ total: 0, days: 0 })));
            columns.forEach((col, x) => {
                const dow = WEEKDAYS.indexOf(col.dayOfWeek);
                if (dow < 0) return;
                const blockTotals = BLOCKS.map(() => 0);
                let dayTotal = 0;
                rows.forEach((row) => {
                    const value = Number(row.data?.[x]) || 0;
                    const block = Math.floor((Number(row.hour) - 1) / BLOCK_HOURS);
                    if (block < 0 || block >= BLOCKS.length) return;
                    blockTotals[block] += value;
                    dayTotal += value;
                });
                // 수집 전(미래) 날짜는 0으로 채워져 오므로 제외
                if (!(dayTotal > 0)) return;
                blockTotals.forEach((total, b) => {
                    sums[b][dow].total += total;
                    sums[b][dow].days += 1;
                });
            });
            const cells = [];
            sums.forEach((byDow, b) =>
                byDow.forEach((s, dow) => {
                    if (s.days > 0) cells.push([dow, b, Math.round((s.total / s.days) * 10) / 10, s.days]);
                })
            );
            return cells;
        },
        peakCell() {
            if (!this.cells.length) return null;
            const top = this.cells.reduce((a, b) => (b[2] > a[2] ? b : a));
            return { dow: `${WEEKDAYS[top[0]]}요일`, block: BLOCKS[top[1]] };
        },
        option() {
            const values = this.cells.map((c) => c[2]);
            const unit = this.grid?.unit || this.unit;
            return {
                backgroundColor: "transparent",
                grid: { left: 8, right: 8, top: 4, bottom: 4, containLabel: true },
                tooltip: tooltipStyle({
                    appendTo: "body",
                    formatter: (p) => {
                        const [dow, block, value, days] = p.value;
                        return (
                            tooltipHtml.title(`${WEEKDAYS[dow]}요일 ${BLOCKS[block]}`) +
                            tooltipHtml.bodyOpen() +
                            tooltipHtml.row("", "평균 사용량", `${num(value, 1)} ${unit}`) +
                            tooltipHtml.row("", "기준 일수", `${days}일`) +
                            tooltipHtml.bodyClose()
                        );
                    },
                }),
                xAxis: {
                    type: "category",
                    data: WEEKDAYS,
                    position: "bottom",
                    axisLabel: axisLabel({
                        color: (v) => (v === "일" ? chartColors.weekend : v === "토" ? chartColors.saturday : grayScale[0]),
                    }),
                    axisLine: { show: false },
                    axisTick: { show: false },
                },
                yAxis: {
                    type: "category",
                    data: BLOCKS,
                    inverse: true,
                    axisLabel: axisLabel(),
                    axisLine: { show: false },
                    axisTick: { show: false },
                },
                visualMap: {
                    show: false,
                    dimension: 2,
                    min: Math.min(...values),
                    max: Math.max(...values, 1),
                    inRange: { color: navyRamp },
                },
                series: [
                    {
                        type: "heatmap",
                        data: this.cells,
                        itemStyle: { borderColor: "#ffffff", borderWidth: 3, borderRadius: 4 },
                        emphasis: { itemStyle: { borderColor: chartColors.text, borderWidth: 1 } },
                    },
                ],
            };
        },
    },
};
</script>

<style scoped>
.heatmap-peak {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    white-space: nowrap;
}
</style>
